import React, { useEffect, useState, useCallback, useContext, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PatientDataContext } from '../../../context/PatientDataContext';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import { CheckCircleIcon, ChevronLeftIcon, CheckIcon, PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid';
import { SearchIcon, ClockIcon, XIcon } from '@heroicons/react/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { Tooltip } from 'react-tooltip';
import InfoTooltip from '../../../components/InfoTooltip';
import { FormattedAnswer, normalizeRawAnswer, buildDisplayAnswer } from '../../../utils/formDisplayUtils';
import { formatDateTime } from '../../../utils/timestampUtils';
import { auth, db } from '../../../firebase';
import { serverTimestamp, doc, runTransaction, updateDoc, deleteField } from 'firebase/firestore';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, DialogMessage } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import DictationTextArea from '../../../components/DictationTextArea';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { buildFirstGeneratedFields, buildFirstGeneratedFieldsDotNotation, buildFirstCompletedFields, buildFirstCompletedFieldsDotNotation, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { TriggerSembleMdtReport } from '../../../utils/TriggerSembleMdtReport';
import { TriggerRenderSembleMdtReport } from '../../../utils/TriggerRenderSembleMdtReport';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import DDSideNavbar from './components/DDSideNavbar';
import {
  buildDDNavSections,
  getDDRouteForSectionIndex,
  hasActionableOtherConditions,
  isAdhdPositiveDecision,
} from '../../../utils/diagnosticDecisionRouting';
import {
  buildMdtPlanItemGroups,
  buildTreatmentPlanningGroup,
  getGoalsSubsectionMeta,
  getPadSectionMeta,
  getEffectiveSelectedTreatments,
  TREATMENT_PLANNING_PHARMACOLOGICAL_FIELD,
} from '../../../utils/mdtPlanUtils';

/** Pill labels for the plan toggle — selected state uses a different label. */
const ADD_TO_PLAN_LABEL = 'Add to plan';
const ADDED_TO_PLAN_LABEL = 'Added to plan';

/** Shared "Add to plan" pill rendered inside item rows. */
const AddToPlanPill = React.memo(function AddToPlanPill({ isSelected, canEdit, onToggle }) {
  return (
    <li
      onClick={canEdit ? onToggle : undefined}
      className={`
        flex-1 p-2 w-64
        rounded-lg transition-all duration-300 ease-in-out
        text-center
        flex flex-row items-center justify-center gap-2
        ${!canEdit
          ? 'cursor-not-allowed opacity-75'
          : 'cursor-pointer group'
        }
        ${isSelected
          ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
          : canEdit
            ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
            : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
        }
      `}
    >
      <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
        {isSelected ? <CheckIcon className="w-4 h-4" /> : <PlusIcon className="w-4 h-4" />}
      </div>
      <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
        {isSelected ? ADDED_TO_PLAN_LABEL : ADD_TO_PLAN_LABEL}
      </span>
    </li>
  );
});

/**
 * StrategyItemRow — one strategy/recommendation with an "Add to plan" toggle.
 * Defined outside the parent component so React preserves identity across
 * renders (prevents unmount/remount cycles that break click handling and cause
 * Radix Popover portal errors).
 */
const StrategyItemRow = React.memo(function StrategyItemRow({
  title,
  field,
  tooltipText,
  isSelected,
  canEdit,
  onToggle,
}) {
  const handleClick = useCallback(() => onToggle(field), [onToggle, field]);

  return (
    <div className="mb-4 flex items-start">
      <div className="w-96 flex flex-col items-start">
        <div className="flex items-start gap-2 w-full">
          {tooltipText ? (
            <InfoTooltip
              content={tooltipText}
              label={`Information about ${title}`}
              maxWidth="300px"
              className="flex-shrink-0 mt-0.5"
            />
          ) : (
            <div className="w-4 h-4 flex-shrink-0" />
          )}
          <h4 className="text-left font-semibold text-gray-700 flex-1 min-w-0">
            {title}
          </h4>
        </div>
      </div>
      <div className="ml-5">
        <ul className="items-center flex flex-row gap-4">
          <AddToPlanPill isSelected={isSelected} canEdit={canEdit} onToggle={handleClick} />
        </ul>
      </div>
    </div>
  );
});

/**
 * TreatmentItemRow — one pharmacological option with "Add to plan" toggle and a
 * compact notes field to its right.  Uses local note state during typing so
 * keystrokes do not re-render every sibling row (which caused page scroll jumps
 * when DictationTextArea recalculated heights across the list).  Notes sync to
 * the parent on blur, matching the Patient Goals save pattern.
 */
const TreatmentItemRow = React.memo(function TreatmentItemRow({
  title,
  field,
  tooltipText,
  isSelected,
  noteValue,
  canEdit,
  onToggle,
  onNoteBlur,
}) {
  const [localNote, setLocalNote] = useState(noteValue || '');
  const tooltipId = `mdt-plan-${field}-tooltip`;

  // Re-hydrate when Firestore data changes (initial load / background refetch).
  useEffect(() => {
    setLocalNote(noteValue || '');
  }, [noteValue]);

  const notesEditable = canEdit && isSelected;
  const handleToggleClick = useCallback(() => onToggle(field), [onToggle, field]);
  const handleBlur = useCallback(() => onNoteBlur(field, localNote), [onNoteBlur, field, localNote]);

  return (
    <div className="mb-4 flex items-start">
      <div className="w-96 flex flex-col items-start">
        <div className="flex items-start gap-2 w-full">
          {tooltipText ? (
            <>
              <QuestionMarkCircleIcon
                className="w-4 h-4 flex-shrink-0 mt-0.5 text-violet-700 cursor-help"
                data-tooltip-id={tooltipId}
                data-tooltip-content={tooltipText}
              />
              <Tooltip
                id={tooltipId}
                place="top"
                style={{
                  maxWidth: '300px',
                  whiteSpace: 'normal',
                  backgroundColor: 'rgba(34, 34, 34, 1)',
                  borderRadius: '15px',
                  color: '#fff',
                  fontSize: '0.8em',
                }}
              />
            </>
          ) : (
            <div className="w-4 h-4 flex-shrink-0" />
          )}
          <h4 className="text-left font-semibold text-gray-700 flex-1 min-w-0">
            {title}
          </h4>
        </div>
      </div>
      <div className="ml-5 flex flex-1 items-start gap-4 min-w-0">
        <ul className="items-center flex flex-row gap-4 flex-shrink-0 pt-1">
          <AddToPlanPill isSelected={isSelected} canEdit={canEdit} onToggle={handleToggleClick} />
        </ul>
        <div className="flex-1 min-w-0">
          <DictationTextArea
            value={localNote}
            onChange={setLocalNote}
            onBlur={handleBlur}
            canEdit={notesEditable}
            placeholder="Optional: note the medication plan (e.g. dose, frequency). This is summarised into the report for you to review and approve."
            ariaLabel={`Medication plan notes for ${title}`}
            minHeight="auto"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
});

/**
 * MDT Plan — the final Diagnostic Decision page for Semble MDT Report orgs
 * (Well Life Clinic), shown only for ADHD-positive outcomes.
 *
 * At the end of the DD, the WLC clinician records the plan that will go into
 * the Semble MDT Report (the copy-paste document for their Semble EHR):
 *   1. Patient's Goals — an "early" version of the PAD goals notes, captured
 *      now so the report's Plan section can include a goals paragraph before
 *      the PAD takes place.
 *   2. "Add to plan" selections — the clinician flags which Strategies
 *      and Recommendations items and which Treatment Options they intend to
 *      cover in the Post-Assessment Discussion.  The item lists reuse the
 *      patient's own PAD schema (dataModel.5_postAssessmentDiscussion) so the
 *      labels and ordering exactly match what the PAD pages will show.
 *
 * Data is stored under dataModel.4_diagnosticDecision.mdtPlan:
 *   { patientGoalsNotes: string,
 *     toDiscussStrategies: { [firestoreFieldName]: boolean },
 *     toDiscussTreatments: { [firestoreFieldName]: boolean } }
 * Selections are keyed by the stable firestoreFieldName (not the display
 * title) so later label edits cannot orphan stored selections.
 *
 * MODULE COMPLETION IS DEFERRED TO THIS PAGE for ADHD-positive WLC patients:
 * 1_DD/2_CSD navigate here instead of running their completion transaction,
 * and the identical transaction (DD completed + PAD unlock + ddReport flags)
 * runs from this page's completion dialog.  After completion we fire the
 * generateSembleMdtReport Cloud Function so the Documents Dashboard row shows
 * "AI outputs ready for review" when the clinician arrives.
 */
export default function MDTPlan() {
  const { patientId } = useParams();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);
  const navigate = useNavigate();

  // Same edit permission as the rest of the DD module.
  const canEdit = useCanEditStage(patientDataModel, 'DD');

  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Local editing state, hydrated once from Firestore below.
  const [goalsNotes, setGoalsNotes] = useState('');
  const [toDiscussStrategies, setToDiscussStrategies] = useState({});
  const [toDiscussTreatments, setToDiscussTreatments] = useState({});
  // Per-medication free-text notes (keyed by firestoreFieldName).  Each note
  // feeds the LLM generation of that medication's Semble MDT Report paragraph.
  const [treatmentNotes, setTreatmentNotes] = useState({});
  const hydratedRef = useRef(false);

  // Source-dialog state for the assessment-goals prefill row (same pattern as REV.js).
  const [openGoalsSourceDialog, setOpenGoalsSourceDialog] = useState(false);
  const [rawPrefillData, setRawPrefillData] = useState([]);
  const [dialogPrefillText, setDialogPrefillText] = useState('');

  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // Hydrate local state from the stored mdtPlan exactly once per mount so
  // in-progress typing is never clobbered by background refetches.
  useEffect(() => {
    if (hydratedRef.current) return;
    const mdtPlan = patientDataModel?.dataModel?.['4_diagnosticDecision']?.mdtPlan;
    if (!patientDataModel) return;
    hydratedRef.current = true;
    setGoalsNotes(mdtPlan?.patientGoalsNotes || '');
    setToDiscussStrategies(mdtPlan?.toDiscussStrategies || {});
    setToDiscussTreatments(mdtPlan?.toDiscussTreatments || {});
    setTreatmentNotes(mdtPlan?.treatmentNotes || {});
  }, [patientDataModel]);

  const hasOtherConditions = useMemo(() => (
    hasActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
    })
  ), [patientDataModel, wf.isFlatDifferentials, wf.skipDifferentialsDecisions]);

  // Read section 1 (ADHD decision) early -- needed by both the route guard
  // and sidebar computation below.
  const ddSection1 = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'];
  const hasMdtPlan = wf.isSembleMdtReportEnabled && isAdhdPositiveDecision(ddSection1);

  // Defensive route guard: this page only exists for Semble MDT Report orgs
  // with a positive ADHD decision. If the feature is disabled or the ADHD
  // decision flips to "Not met" (e.g. edited on another tab), redirect back
  // to the primary DD page rather than leaving a dead-end page accessible.
  // We wait for patientDataModel to load first — the workflow config resolves
  // from patient data, so redirecting before load would misfire for eligible
  // patients.
  useEffect(() => {
    if (!patientDataModel) return;
    if (hasMdtPlan) return;
    navigate(
      `/patient/${patientId}/proforma/diagnostic-decision/${wf.isFlatDifferentials ? 'adhd' : 'primary-diagnosis'}`,
      { replace: true }
    );
  }, [patientDataModel, hasMdtPlan, wf.isFlatDifferentials, navigate, patientId]);
  const mdtPlanSectionIndex = hasOtherConditions ? 4 : 3;

  const ddNavSections = useMemo(() => {
    if (!wf.hasMultiStepDD) return null;
    return buildDDNavSections({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      isTOCWorkflow: wf.isTOCWorkflow,
      isSLaMWorkflow: wf.isSLaMWorkflow,
      isMDTReview: wf.isMDTReview,
      isSembleMdtReportEnabled: wf.isSembleMdtReportEnabled,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
      currentSectionIndex: mdtPlanSectionIndex,
    });
  }, [wf.hasMultiStepDD, wf.isTOCWorkflow, wf.isSLaMWorkflow, wf.isMDTReview, wf.isSembleMdtReportEnabled, patientDataModel, wf.isFlatDifferentials, wf.skipDifferentialsDecisions, mdtPlanSectionIndex]);

  const navigateToDDSection = useCallback(async (index) => {
    flushPendingUpdates();
    navigate(getDDRouteForSectionIndex(patientId, index, hasOtherConditions, hasMdtPlan));
  }, [patientId, navigate, flushPendingUpdates, hasOtherConditions, hasMdtPlan]);

  // Selectable item groups derived from the patient's own PAD schema copy.
  // Strategies = PAD section 2 (Disclosure / Lifestyle / Environmental);
  // Treatments = PAD section 3 subsections 1-2 (Pharmacological +
  // Non-Pharmacological).  Pure derivation lives in mdtPlanUtils so it can be
  // unit-tested directly.
  const padStage = patientDataModel?.dataModel?.['5_postAssessmentDiscussion'];
  const strategyGroups = useMemo(() => buildMdtPlanItemGroups(padStage, '2', ['1', '2', '3']), [padStage]);
  const treatmentPlanningGroup = useMemo(() => buildTreatmentPlanningGroup(), []);
  // Only pharmacological options (subsection 1) — per-medication paragraphs
  // feed the Semble report; the bottom panel is gated on the high-level
  // "Pharmacological Options" toggle in the Treatment Planning strategy panel.
  const treatmentGroups = useMemo(() => buildMdtPlanItemGroups(padStage, '3', ['1']), [padStage]);
  const isPharmacologicalPlanningSelected =
    toDiscussStrategies[TREATMENT_PLANNING_PHARMACOLOGICAL_FIELD] === true;
  const goalsSubsectionMeta = useMemo(() => getGoalsSubsectionMeta(padStage), [padStage]);
  const strategiesSectionMeta = useMemo(() => getPadSectionMeta(padStage, '2'), [padStage]);

  const ciGoalsQuestion = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.['2']
    ?.subsections?.['1']?.subsectionQuestions?.['4'];

  // Structured raw answer for the assessment-goals prefill row (REV.js parity).
  const rawAssessmentGoalsAnswer = useMemo(() => {
    const questions = patientDataModel?.formData?.yourCurrentConcerns?.formSubsections?.[1]?.questions;
    if (!questions) return null;

    for (const question of Object.values(questions)) {
      for (const [subKey, subquestion] of Object.entries(question.subquestions || {})) {
        if (Number.isNaN(parseInt(subKey, 10))) continue;
        if (subquestion.serviceUserVarName === 'assessmentGoals') {
          return normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer) ?? null;
        }
      }
    }
    return null;
  }, [patientDataModel?.formData?.yourCurrentConcerns]);

  /**
   * Pull raw service-user form answers that fed the CI assessment-goals prefill.
   * Copied from REV.js so the source dialog on the MDT Plan page matches the PAD.
   */
  const getRawPrefillInputs = useCallback((varNames) => {
    if (!patientDataModel?.formData?.yourCurrentConcerns?.formSubsections?.[1]?.questions
      || !varNames || varNames.length === 0) {
      return [];
    }

    const results = [];
    const aboutYourReferralQuestions = patientDataModel.formData.yourCurrentConcerns.formSubsections[1].questions;

    Object.entries(aboutYourReferralQuestions).forEach(([questionKey, question]) => {
      if (question.subquestions) {
        Object.entries(question.subquestions).forEach(([subquestionKey, subquestion]) => {
          if (Number.isNaN(parseInt(subquestionKey, 10))) return;

          if (varNames.includes(subquestion.serviceUserVarName)) {
            const normalized = normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer);
            const allOptions = [...(subquestion.options || []), ...(subquestion.noneOptions || [])];

            results.push({
              formName: 'Your Current Concerns',
              questionTitle: question.questionTitle,
              question: subquestion.subquestionDisplayText,
              answer: buildDisplayAnswer(normalized),
              hasAnswer: normalized !== null && normalized !== undefined && normalized !== '',
              lastUpdated: question.lastUpdated,
              updatedBy: null,
              inputType: subquestion.inputType,
              options: allOptions,
              rawAnswer: normalized,
              otherOptions: subquestion.otherOptions || {},
              wasSkipped: question.wasSkipped,
              skipButtonText: question.skipButtonText,
            });
          }
        });
      }
    });

    return results;
  }, [patientDataModel]);

  const isOptionSelected = useCallback((option, rawAnswer) => {
    if (!rawAnswer) return false;
    if (typeof rawAnswer === 'string') return rawAnswer === option;
    if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'string') return rawAnswer.includes(option);
    if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'object') {
      return rawAnswer.some((ans) => ans.value === option);
    }
    if (rawAnswer && typeof rawAnswer === 'object' && 'value' in rawAnswer) {
      return rawAnswer.value === option;
    }
    return false;
  }, []);

  const handleOpenRawPrefillDialog = useCallback(() => {
    if (!ciGoalsQuestion) return;

    const varNames = ciGoalsQuestion.prefilledByServiceUserVarNames || [];

    if (varNames.length > 0 && !ciGoalsQuestion.clinicianInput) {
      setRawPrefillData(getRawPrefillInputs(varNames));
      setDialogPrefillText(ciGoalsQuestion.patientPrefill || '');
      setOpenGoalsSourceDialog(true);
    } else {
      setRawPrefillData([{
        formName: ciGoalsQuestion.clinicianInput ? 'Clinician Input' : 'Your Current Concerns',
        question: ciGoalsQuestion.title,
        answer: ciGoalsQuestion.clinicianInput || ciGoalsQuestion.patientPrefill || 'No previous answer given',
        hasAnswer: !!(ciGoalsQuestion.clinicianInput || ciGoalsQuestion.patientPrefill),
        lastUpdated: ciGoalsQuestion.clinicianInputUpdatedAt || ciGoalsQuestion.patientPrefillLastUpdatedAt,
        updatedBy: ciGoalsQuestion.clinicianInput
          ? (ciGoalsQuestion.clinicianInputUpdatedByName || 'Unknown clinician')
          : null,
      }]);
      setDialogPrefillText(ciGoalsQuestion.patientPrefill || '');
      setOpenGoalsSourceDialog(true);
    }
  }, [ciGoalsQuestion, getRawPrefillInputs]);

  // ── Persistence ───────────────────────────────────────────────────────────
  // Goals notes save on blur; toggles save immediately on click.  Both go
  // through the standard debounced PatientDataContext queue.
  const handleGoalsBlur = useCallback(() => {
    if (!canEdit) return;
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        mdtPlan: { patientGoalsNotes: goalsNotes },
      },
    });

    // Staleness propagation: if the Semble MDT Report paragraphs were already
    // generated and the clinician edits their goals after completing the DD,
    // the goals paragraph is stale.  Compare current notes against the input
    // used for the last generation (not the live Firestore mdtPlan value, which
    // may already reflect this edit).  Clear only the goals paragraph's approval
    // and mark the overall report as unapproved so the dashboard shows "ready
    // for review".  Other paragraphs' approvals are preserved — on re-completion
    // only the goals paragraph will be regenerated (via forceRegenerateKeys).
    const reports = patientDataModel?.dataModel?.['6_manageReports'];
    // The stored input is a dict: { mdtPlanGoals: { value: "...", source: "..." } }
    const storedGoalsObj = reports?.sections?.['10']?.inputs
      ?.semble_patient_goals?.['Clinician Notes'];
    const lastGoalsInput = (typeof storedGoalsObj === 'object' && storedGoalsObj !== null)
      ? (storedGoalsObj?.mdtPlanGoals?.value || '')
      : (storedGoalsObj || '');
    if (wf.isSembleMdtReportEnabled && reports?.sembleMDTReportGenerated && goalsNotes !== lastGoalsInput) {
      updateDoc(doc(db, 'patients', patientId), {
        'dataModel.6_manageReports.sections.10.approvedParagraphs.semble_patient_goals': deleteField(),
        'dataModel.6_manageReports.sembleMDTReportApproved': false,
        'dataModel.6_manageReports.sembleMDTReportApprovedBy': null,
        'dataModel.6_manageReports.sembleMDTReportApprovedByName': null,
        'dataModel.6_manageReports.sembleMDTReportApprovedAt': null,
      }).catch((error) => {
        console.error('Failed to reset Semble MDT Report staleness flags:', error);
      });
    }
  }, [canEdit, patientId, goalsNotes, updatePatientFirestore, patientDataModel, wf.isSembleMdtReportEnabled]);

  // Per-medication note persistence — same blur-save + staleness pattern as goals.
  // TreatmentItemRow always passes localNote as noteOverride, so we never fall
  // back to reading treatmentNotes state inside this callback.  Using a ref for
  // patientDataModel avoids recreating this callback on every context update,
  // keeping the onNoteBlur prop stable for React.memo.
  const patientDataRef = useRef(patientDataModel);
  useEffect(() => { patientDataRef.current = patientDataModel; }, [patientDataModel]);

  const handleTreatmentNoteBlur = useCallback((field, noteOverride) => {
    if (!canEdit) return;
    const noteValue = noteOverride !== undefined ? noteOverride : '';
    setTreatmentNotes((prev) => ({ ...prev, [field]: noteValue }));
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        mdtPlan: { treatmentNotes: { [field]: noteValue } },
      },
    });

    // Staleness propagation: if the Semble MDT Report was already generated and
    // this medication's paragraph input has changed, clear that paragraph's
    // approval so the dashboard shows "ready for review" for this paragraph.
    const reports = patientDataRef.current?.dataModel?.['6_manageReports'];
    const code = `semble_treatment_${field}`;
    const storedInputs = reports?.sections?.['10']?.inputs?.[code]?.['Clinician Notes'];
    const lastNoteInput = (typeof storedInputs === 'object' && storedInputs !== null)
      ? (storedInputs?.treatmentNote?.value || '')
      : '';
    if (wf.isSembleMdtReportEnabled && reports?.sembleMDTReportGenerated && noteValue !== lastNoteInput) {
      updateDoc(doc(db, 'patients', patientId), {
        [`dataModel.6_manageReports.sections.10.approvedParagraphs.${code}`]: deleteField(),
        'dataModel.6_manageReports.sembleMDTReportApproved': false,
        'dataModel.6_manageReports.sembleMDTReportApprovedBy': null,
        'dataModel.6_manageReports.sembleMDTReportApprovedByName': null,
        'dataModel.6_manageReports.sembleMDTReportApprovedAt': null,
      }).catch((error) => {
        console.error('Failed to reset Semble MDT treatment paragraph staleness:', error);
      });
    }
  }, [canEdit, patientId, updatePatientFirestore, wf.isSembleMdtReportEnabled]);

  // Refs keep handleToggle stable: reading current toggle state from a ref
  // instead of closure means the callback identity never changes when a toggle
  // fires, so downstream React.memo rows only re-render when their own
  // isSelected prop actually flips (via the setter) — not every sibling.
  const strategiesRef = useRef(toDiscussStrategies);
  useEffect(() => { strategiesRef.current = toDiscussStrategies; }, [toDiscussStrategies]);
  const treatmentsRef = useRef(toDiscussTreatments);
  useEffect(() => { treatmentsRef.current = toDiscussTreatments; }, [toDiscussTreatments]);

  const handleToggle = useCallback((kind, field) => {
    if (!canEdit) return;
    const isStrategies = kind === 'strategies';
    const current = isStrategies ? strategiesRef.current : treatmentsRef.current;
    const setter = isStrategies ? setToDiscussStrategies : setToDiscussTreatments;
    const storageKey = isStrategies ? 'toDiscussStrategies' : 'toDiscussTreatments';

    const newValue = !current[field];
    setter({ ...current, [field]: newValue });
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        mdtPlan: { [storageKey]: { [field]: newValue } },
      },
    });
  }, [canEdit, patientId, updatePatientFirestore]);

  // The Plan section of the Semble MDT Report needs the goals paragraph, so
  // goals notes are required before the module can complete.  The To-discuss
  // toggles are optional (a clinician may legitimately select none).
  const isFormValid = Boolean(goalsNotes && goalsNotes.trim());

  // ── Module completion ─────────────────────────────────────────────────────
  const handleFinishMdtPlan = useCallback(async () => {
    if (!canEdit || isSaving) return;
    document.activeElement?.blur();
    await flushPendingUpdates();
    setIsSaving(true);
    try {
      await fetchPatientData(patientId);
      setIsSaving(false);
      setIsDialogOpen(true);
    } catch (error) {
      console.error('Failed to refresh patient data:', error);
      setIsSaving(false);
    }
  }, [canEdit, isSaving, patientId, fetchPatientData, flushPendingUpdates]);

  /**
   * Complete the Diagnostic Decision module from the MDT Plan page.
   *
   * Runs the same completion transaction 1_DD/2_CSD would have run (DD
   * completed + PAD unlock + ddReport generated flags + write-once first*
   * fields), then fires the Semble MDT Report paragraph generation — the CF
   * is gated on DD completion, so this ordering matters.
   */
  const handleNavigationChoice = async (choice) => {
    if (!canEdit) return;
    document.activeElement?.blur();
    setIsNavigating(true);

    try {
      // Ensure the goals notes + toggles are committed before completing.
      await flushPendingUpdates({ noTimeout: true });

      const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
      const ddFirstFields = buildFirstGeneratedFieldsDotNotation('dd', manageReports);
      const ddFirstCompletedFields = buildFirstCompletedFieldsDotNotation('4_diagnosticDecision', patientDataModel?.dataModel?.['4_diagnosticDecision']);
      const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['4_diagnosticDecision']);

      // Only unlock PAD on first completion — re-completing the MDT Plan (to
      // edit selections/notes) must not regress a PAD that is already in
      // progress or completed.
      const padStatus = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.status;
      const isFirstCompletion = !padStatus || padStatus === 'locked';

      try {
        await runTransaction(db, async (transaction) => {
          const docRef = doc(db, 'patients', patientId);
          const updates = {
            'dataModel.4_diagnosticDecision.status': 'completed',
            'dataModel.4_diagnosticDecision.progress': 100,
            'dataModel.4_diagnosticDecision.docs_available': true,
            'dataModel.4_diagnosticDecision.lastCompletedAt': serverTimestamp(),
            'dataModel.4_diagnosticDecision.lastCompletedBy': auth.currentUser.uid,
            'dataModel.4_diagnosticDecision.lastCompletedByName': auth.currentUser.displayName,
            // Mark section 4 (MDT Plan) as completed so buildDDNavSections
            // can read section-level status consistently with sections 1-3.
            'dataModel.4_diagnosticDecision.sections.4.status': 'completed',
            ...ddFirstCompletedFields,
            'dataModel.6_manageReports.ddReportGenerated': true,
            'dataModel.6_manageReports.ddReportGeneratedAt': serverTimestamp(),
            'dataModel.6_manageReports.ddReportGeneratedBy': auth.currentUser.uid,
            'dataModel.6_manageReports.ddReportGeneratedByName': auth.currentUser.displayName,
            ...ddFirstFields,
          };
          if (isFirstCompletion) {
            updates['dataModel.5_postAssessmentDiscussion.status'] = 'notStarted';
          }
          transaction.update(docRef, updates);
        });
      } catch (transactionError) {
        console.warn('Direct transaction failed, falling back to updatePatientFirestore:', transactionError);
        const fallbackUpdates = {
          '4_diagnosticDecision': {
            status: 'completed',
            progress: 100,
            docs_available: true,
            lastCompletedAt: serverTimestamp(),
            lastCompletedBy: auth.currentUser.uid,
            lastCompletedByName: auth.currentUser.displayName,
            sections: { '4': { status: 'completed' } },
            ...firstCompletedFields,
          },
          '6_manageReports': {
            ddReportGenerated: true,
            ddReportGeneratedAt: serverTimestamp(),
            ddReportGeneratedBy: auth.currentUser.uid,
            ddReportGeneratedByName: auth.currentUser.displayName,
            ...buildFirstGeneratedFields('dd', manageReports),
          },
        };
        if (isFirstCompletion) {
          fallbackUpdates['5_postAssessmentDiscussion'] = { status: 'notStarted' };
        }
        await updatePatientFirestore(patientId, fallbackUpdates);
        await flushPendingUpdates({ noTimeout: true });
      }

      appendReportAuditEntry(patientId, {
        module: 'dd',
        eventType: 'generated',
        isFirstOccurrence: !manageReports?.ddReportFirstGeneratedAt,
      });

      // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'dd',
        didWriteFirstCompleted: Object.keys(ddFirstCompletedFields).length > 0,
        enabled: wf.isAppointmentDatesEnabled,
      });

      // DD is now complete — kick off the Semble MDT Report AI paragraphs for
      // patients with the stamped sembleMdtReport flag only.
      if (wf.isSembleMdtReportEnabled) {
        // On re-completion (report was already generated before), perform per-
        // paragraph dirty checks: compare goals and treatment notes against the
        // inputs used for the last generation.  Only regenerate paragraphs whose
        // inputs actually changed, preserving approvals for unchanged content.
        const isReCompletion = manageReports?.sembleMDTReportGenerated === true;
        if (isReCompletion) {
          // Separate changes into two categories:
          // - regenKeys: codes needing AI generation (new selections, note edits)
          // - orphanKeys: deselected treatment codes (just need cleanup)
          //
          // This distinction drives three different completion paths:
          // 1. regenKeys present  → CF regenerates + cleans orphans; no render
          //    (clinician must review new paragraphs first)
          // 2. orphans only       → frontend deletes orphan fields + re-renders
          //    (remaining paragraphs are still approved, no review needed)
          // 3. nothing dirty      → re-render for updated plan list selections
          const regenKeys = [];
          const orphanKeys = [];

          // Goals dirty check
          const storedGoalsObj = manageReports?.sections?.['10']?.inputs
            ?.semble_patient_goals?.['Clinician Notes'];
          const lastGoalsInput = (typeof storedGoalsObj === 'object' && storedGoalsObj !== null)
            ? (storedGoalsObj?.mdtPlanGoals?.value || '')
            : (storedGoalsObj || '');
          if ((goalsNotes || '') !== (lastGoalsInput || '')) {
            regenKeys.push('semble_patient_goals');
          }

          // Effective medication selections: when the high-level "Pharmacological
          // Options" toggle is off, individual medication selections are ignored
          // even though they remain stored (the panel is just greyed out).  This
          // ensures unticking "Pharmacological Options" after generation cleans
          // up the previously generated Medication Planning paragraphs rather
          // than leaving them stranded in the final report.
          const effectiveTreatments = getEffectiveSelectedTreatments({
            toDiscussStrategies,
            toDiscussTreatments,
          });

          // Treatment paragraphs: detect new selections and note changes
          const storedOutputs = manageReports?.sections?.['10']?.outputs || {};
          const storedInputs = manageReports?.sections?.['10']?.inputs || {};
          for (const [field, isSelected] of Object.entries(effectiveTreatments)) {
            if (!isSelected) continue;
            const code = `semble_treatment_${field}`;
            const currentNote = (treatmentNotes[field] || '').trim();
            if (!storedOutputs[code]) {
              regenKeys.push(code);
            } else {
              const storedNote = storedInputs[code]?.['Clinician Notes']?.treatmentNote?.value || '';
              if (currentNote !== storedNote.trim()) {
                regenKeys.push(code);
              }
            }
          }

          // Detect deselected treatment codes (orphans).  Any stored treatment
          // paragraph whose field is no longer in the EFFECTIVE selections is an
          // orphan — this covers both individually-deselected medications and
          // the whole panel being disabled via the pharmacological toggle.
          for (const key of Object.keys(storedOutputs)) {
            if (!key.startsWith('semble_treatment_')) continue;
            const field = key.replace('semble_treatment_', '');
            if (!effectiveTreatments[field]) {
              orphanKeys.push(key);
            }
          }

          if (regenKeys.length > 0) {
            // AI regeneration required — send all keys (regen + orphans) to the
            // CF.  It generates new paragraphs and cleans up orphans atomically.
            // Do NOT trigger render: the clinician must review/approve the newly
            // generated paragraphs before the render can succeed (otherwise the
            // render CF rejects with FAILED_PRECONDITION).
            // waitAndRetry: if another generation is in flight (e.g. physical-
            // health regen fired from CI exit moments earlier), wait for it to
            // finish and then retry, matching the cover-page/patient-report
            // pattern for rapid successive re-finalisations (max 2 runs).
            TriggerSembleMdtReport({
              clinicianId: auth.currentUser.uid,
              patientId,
              forceRegenerateKeys: [...regenKeys, ...orphanKeys],
              waitAndRetry: true,
            });
          } else if (orphanKeys.length > 0) {
            // Only deselected medications — no AI generation needed.  Delete the
            // orphan output/input/approval fields directly on the frontend so the
            // remaining (still-approved) paragraphs stay intact and no review is
            // required.  Then re-render the document to exclude the removed
            // treatment paragraph(s) from the DOCX/PDF.
            const orphanUpdates = {};
            for (const code of orphanKeys) {
              orphanUpdates[`dataModel.6_manageReports.sections.10.outputs.${code}`] = deleteField();
              orphanUpdates[`dataModel.6_manageReports.sections.10.inputs.${code}`] = deleteField();
              orphanUpdates[`dataModel.6_manageReports.sections.10.approvedParagraphs.${code}`] = deleteField();
            }
            updateDoc(doc(db, 'patients', patientId), orphanUpdates)
              .then(() => {
                if (manageReports?.sembleMDTReportApproved) {
                  TriggerRenderSembleMdtReport({
                    clinicianId: auth.currentUser.uid,
                    patientId,
                  });
                }
              })
              .catch((err) => console.error('Error cleaning up orphan treatment paragraphs:', err));
          } else if (manageReports?.sembleMDTReportApproved) {
            // No AI paragraph changes — only plan list selections or strategy/
            // recommendation toggles changed.  Re-render the DOCX/PDF so the
            // plan lists reflect the current selections.
            TriggerRenderSembleMdtReport({
              clinicianId: auth.currentUser.uid,
              patientId,
            });
          }
        } else {
          TriggerSembleMdtReport({ clinicianId: auth.currentUser.uid, patientId });
        }
      }

      if (choice === 'documents') {
        setIsDialogOpen(false);
        setIsGenerating(true);
      } else if (choice === 'pad') {
        setIsDialogOpen(false);
        navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
      }
    } catch (error) {
      console.error('Error during navigation:', error);
      setIsNavigating(false);
      alert('An error occurred. Please try again.');
    }
  };

  const handleGenerateReport = async () => handleNavigationChoice('documents');
  const handleGoToPostAssessmentDiscussion = async () => handleNavigationChoice('pad');
  const handleCancelGeneration = () => setIsGenerating(false);

  // Back route mirrors how the clinician arrived: in multi-step DD, MDT Plan
  // follows Final Formulation; in non-multi-step (legacy WLC v1), it follows
  // Other Conditions or the ADHD decision page directly.
  const backRoute = wf.hasMultiStepDD
    ? `/patient/${patientId}/proforma/diagnostic-decision/final-formulation`
    : hasOtherConditions
      ? `/patient/${patientId}/proforma/diagnostic-decision/${wf.isFlatDifferentials ? 'other-conditions' : 'secondary-diagnoses'}`
      : `/patient/${patientId}/proforma/diagnostic-decision/${wf.isFlatDifferentials ? 'adhd' : 'primary-diagnosis'}`;
  const backLabel = wf.hasMultiStepDD ? 'Final Formulation' : hasOtherConditions
    ? (wf.isFlatDifferentials ? 'Back to Decision for Other Conditions' : 'Back to Secondary Diagnoses')
    : (wf.isFlatDifferentials ? 'Back to Decision for ADHD' : 'Back to Primary Diagnosis');

  const handleBack = () => {
    flushPendingUpdates();
    navigate(backRoute);
  };

  // ── Stable toggle callbacks for child components ─────────────────────────
  // A single callback per "kind" that accepts the field name — stable across
  // renders so React.memo on child rows is not defeated by new fn references.
  const handleStrategyToggle = useCallback((field) => {
    if (!canEdit) return;
    handleToggle('strategies', field);
  }, [canEdit, handleToggle]);

  const handleTreatmentToggle = useCallback((field) => {
    if (!canEdit || !isPharmacologicalPlanningSelected) return;
    handleToggle('treatments', field);
  }, [canEdit, isPharmacologicalPlanningSelected, handleToggle]);

  // Strip leading numerical prefix (e.g. "2.1 Disclosure" → "Disclosure")
  // from subsection titles inherited from the PAD schema.  The MDT Plan does
  // not use the PAD's hierarchical numbering.
  const stripNumberPrefix = (text) => text?.replace(/^\d+(\.\d+)*\s+/, '') || text;

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Diagnostic Decision'],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className={wf.hasMultiStepDD ? 'flex items-start' : ''}>
        {wf.hasMultiStepDD && ddNavSections && (
          <DDSideNavbar
            sections={ddNavSections}
            currentSectionIndex={mdtPlanSectionIndex}
            navigateToSection={navigateToDDSection}
          />
        )}
      <div className="flex-grow p-8">
        {/* Header row: back button, title, forward button */}
        <div className="flex justify-between items-center mb-3">
          <button
            onClick={handleBack}
            className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
            <span className="font-bold">{backLabel}</span>
          </button>

          <div className="flex items-center">
            <h1 className="text-2xl font-bold">MDT Plan</h1>
          </div>

          {canEdit ? (
            <button
              onClick={handleFinishMdtPlan}
              disabled={!isFormValid || isSaving}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${
                isFormValid && !isSaving
                  ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span className="font-bold">{isSaving ? 'Saving Progress...' : 'Complete MDT Plan'}</span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
          ) : <div className="w-1/4" />}
        </div>

        {/* Patient's Goals — REV.js subsection 2 panel layout */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Patient's Goals</h2>
        </div>

        <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
          <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
            {stripNumberPrefix(goalsSubsectionMeta.subsectionTitleDisplay)}
          </h3>

          {ciGoalsQuestion && (
            <div className="mb-6 mt-2 rounded-lg">
              <div className="text-slate-700 text-base flex text-left items-center mb-2">
                <div className="w-96 flex items-center gap-2">
                  <span className="font-bold">Assessment goals</span>
                  {ciGoalsQuestion.patientPrefill ? (
                    <p className="text-sm italic text-gray-600">(from Patient Self-Report)</p>
                  ) : (
                    <p className="text-sm italic text-gray-400">(No previous answer given by Patient)</p>
                  )}
                </div>

                <div className="flex items-center">
                  <div className="w-96 p-3 border rounded-md bg-gray-100 text-gray-700 cursor-not-allowed whitespace-pre-wrap">
                    <FormattedAnswer
                      rawAnswer={rawAssessmentGoalsAnswer}
                      displayText={ciGoalsQuestion.patientPrefill || 'No answer given'}
                    />
                  </div>

                  <Dialog.Root open={openGoalsSourceDialog} onOpenChange={(open) => !open && setOpenGoalsSourceDialog(false)}>
                    <Dialog.Trigger asChild>
                      <div
                        className="cursor-pointer ml-1 mt-2"
                        onClick={handleOpenRawPrefillDialog}
                      >
                        <div className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100">
                          <SearchIcon className="h-3 w-3" />
                        </div>
                      </div>
                    </Dialog.Trigger>

                    <Dialog.Portal>
                      <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
                      <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                          <Dialog.Title className="text-base font-semibold text-gray-900">
                            Patient Form Responses
                          </Dialog.Title>
                          <Dialog.Close asChild>
                            <button type="button" className="p-1 rounded-full hover:bg-gray-100">
                              <XIcon className="h-5 w-5 text-gray-500" />
                            </button>
                          </Dialog.Close>
                        </div>

                        <div className="mb-4">
                          <h4 className="font-semibold text-sm mb-2">Patient's Goals</h4>

                          {rawPrefillData[0]?.lastUpdated && (
                            <div className="text-xs text-gray-500 mb-3 flex items-center">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              Last updated: {formatDateTime(rawPrefillData[0].lastUpdated)}
                            </div>
                          )}

                          <div className="space-y-2 mt-2">
                            {rawPrefillData.length > 0 ? (
                              rawPrefillData.map((item, i) => (
                                <div
                                  key={i}
                                  className={`border rounded-md p-3 ${item.hasAnswer
                                    ? 'bg-violet-50 shadow-sm border-violet-200'
                                    : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                                  }`}
                                >
                                  <div className="text-xs text-gray-500 mb-0.5">From: {item.formName}</div>
                                  {item.questionTitle && (
                                    <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
                                  )}
                                  <div className="text-xs font-medium mb-1.5">{item.question}</div>
                                  {item.wasSkipped && (
                                    <div className="text-xs text-gray-500 flex justify-start items-center mb-2">
                                      <span>Patient clicked</span>
                                      <span className="text-xs bg-violet-50 shadow-sm border-violet-200 border text-gray-800 font-semibold px-2 py-1 rounded-md ml-2 whitespace-nowrap">
                                        {item.skipButtonText}
                                      </span>
                                    </div>
                                  )}
                                  <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${item.hasAnswer
                                    ? 'bg-white border-gray-200'
                                    : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                                  }`}
                                  >
                                    <FormattedAnswer
                                      rawAnswer={item.rawAnswer}
                                      displayText={rawPrefillData.length === 1 && dialogPrefillText ? dialogPrefillText : item.answer}
                                    />
                                  </div>
                                  {item.options && item.options.length > 0 && (
                                    <div className="mt-2">
                                      <div className="text-xs text-gray-500 mb-1">Available options:</div>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                        {item.options.map((option, optIndex) => {
                                          const isSelected = item.hasAnswer && isOptionSelected(option, item.rawAnswer);
                                          return (
                                            <div
                                              key={optIndex}
                                              className={`text-xs p-1 rounded ${
                                                isSelected
                                                  ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                                                  : 'bg-gray-50 border border-gray-200 text-gray-500'
                                              }`}
                                            >
                                              {option}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <p className="text-gray-500 italic">No source data available</p>
                            )}
                          </div>
                        </div>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>
                </div>
              </div>
            </div>
          )}

          {goalsSubsectionMeta.tooltipText && (
            <p className="text-left text-gray-700">{goalsSubsectionMeta.tooltipText}</p>
          )}

          <div className="text-left mt-6">
            <h4 className="text-gray-700 text-sm font-semibold mb-2">
              {goalsSubsectionMeta.textBoxSubtitle}
              {goalsSubsectionMeta.notesRequired && (
                <span className="text-red-600 ml-1">*</span>
              )}
            </h4>
            <DictationTextArea
              value={goalsNotes}
              onChange={setGoalsNotes}
              onBlur={handleGoalsBlur}
              canEdit={canEdit}
              ariaLabel="Patient's goals"
            />
          </div>
        </div>

        {/* Strategies and Recommendations — DIS.js section layout */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {strategiesSectionMeta.sectionTitle || 'Strategies and Recommendations'}
          </h2>
        </div>
        {strategyGroups.length === 0 ? (
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
            <p className="text-sm italic text-gray-400 text-left">No items available for this patient.</p>
          </div>
        ) : (
          strategyGroups.map((group) => (
            <div key={group.title} className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
              <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                {stripNumberPrefix(group.title)}
              </h3>
              <div className="space-y-4">
                {group.items.map(({ title: itemTitle, field, tooltipText }) => (
                  <StrategyItemRow
                    key={field}
                    title={itemTitle}
                    field={field}
                    tooltipText={tooltipText}
                    isSelected={toDiscussStrategies[field] === true}
                    canEdit={canEdit}
                    onToggle={handleStrategyToggle}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        {/* Treatment Planning — synthetic high-level strategy toggles that
            appear in the Semble report under their own heading. */}
        <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
          <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
            {treatmentPlanningGroup.title}
          </h3>
          <div className="space-y-4">
            {treatmentPlanningGroup.items.map(({ title, field, tooltipText }) => (
              <StrategyItemRow
                key={field}
                title={title}
                field={field}
                tooltipText={tooltipText}
                isSelected={toDiscussStrategies[field] === true}
                canEdit={canEdit}
                onToggle={handleStrategyToggle}
              />
            ))}
          </div>
        </div>

        {/* Medication Planning — TRT.js section layout.  Only active when the
            clinician has added "Pharmacological Options" to the plan above. */}
        <div
          className={`transition-opacity duration-300 ${
            isPharmacologicalPlanningSelected ? '' : 'opacity-50 pointer-events-none'
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">
              Medication Planning
            </h2>
          </div>
          {!isPharmacologicalPlanningSelected && (
            <p className="text-sm italic text-gray-500 text-left mb-4">
              Add Pharmacological Options to the plan above to select specific medications.
            </p>
          )}
          {treatmentGroups.length === 0 ? (
            <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
              <p className="text-sm italic text-gray-400 text-left">No items available for this patient.</p>
            </div>
          ) : (
            treatmentGroups.map((group) => (
              <div key={group.title} className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  {stripNumberPrefix(group.title)}
                </h3>
                <div className="space-y-4">
                  {group.items.map(({ title: itemTitle, field, tooltipText }) => (
                    <TreatmentItemRow
                      key={field}
                      title={itemTitle}
                      field={field}
                      tooltipText={tooltipText}
                      isSelected={toDiscussTreatments[field] === true}
                      noteValue={treatmentNotes[field] || ''}
                      canEdit={canEdit && isPharmacologicalPlanningSelected}
                      onToggle={handleTreatmentToggle}
                      onNoteBlur={handleTreatmentNoteBlur}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Bottom navigation */}
        <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
          <div className="w-1/3 flex justify-start">
            <button
              onClick={handleBack}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">{backLabel}</span>
            </button>
          </div>

          <div className="w-1/3" />

          <div className="w-1/3 flex justify-end">
            {canEdit && (
              <button
                onClick={handleFinishMdtPlan}
                disabled={!isFormValid || isSaving}
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${
                  isFormValid && !isSaving
                    ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <span className="font-bold">{isSaving ? 'Saving Progress...' : 'Complete MDT Plan'}</span>
                {isSaving
                  ? <ButtonSpinner className="w-5 h-5 ml-2" />
                  : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Module completion dialog — same MDT-aware button ordering as the
          other DD pages: MDT Review orgs emphasise "Generate Report". */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="MDT Plan Complete"
        description="You have completed the MDT Plan."
        actions={(
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <PrimaryActionButton
              key="pad"
              label={isNavigating ? 'Saving...' : 'Go to Post-Assessment Discussion'}
              onClick={() => { if (!isNavigating) handleGoToPostAssessmentDiscussion(); }}
              disabled={isNavigating}
              secondary={wf.isMDTReview}
            />
            <GenerateReportButton
              key="generate"
              onClick={() => { if (!isNavigating) handleGenerateReport(); }}
              disabled={isNavigating}
              secondary={!wf.isMDTReview}
              savingLabel={isNavigating ? 'Saving...' : undefined}
            />
          </>
        )}
      >
        <DialogMessage>Are you happy to proceed?</DialogMessage>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Diagnostic Decision"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={() => navigate(`/patient/${patientId}/documents`)}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}
